/**
 * Rodapé do ano — o que a planilha calculava embaixo de cada aba (total, a receber,
 * metas), agora sempre certo: o ticket médio divide pelo nº REAL de pedidos.
 * As cores viram filtro: clique numa para ver só aqueles pedidos.
 */
import { createEffect, createSignal, For, on, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import { Dialog } from '@ui/components/Dialog'
import { Input } from '@ui/components/Input'
import type { ResumoAnoPedidos, StatusPedido } from '@shared/types'
import { api } from '../../nucleo/cliente'
import { mensagemErro } from '../../lib/erro'
import { inteiro, lerNumeroBR, moeda, numero, pct, corExibicao, SEM_COR } from './formato'

export function Rodape(props: {
  resumo: ResumoAnoPedidos | undefined
  status: StatusPedido[] | undefined
  filtroStatus: number | null
  aoFiltrar: (id: number | null) => void
  podeEditar: boolean
  aoMudar: () => void
}) {
  const [metasAberto, setMetasAberto] = createSignal(false)
  const r = () => props.resumo
  return (
    <div class="shrink-0 border-t border-edge bg-panel px-4 py-3 flex flex-col gap-3">
      <div class="flex flex-wrap gap-x-8 gap-y-2">
        <Numero rotulo="Pedidos" valor={r() ? inteiro(r()!.pedidos) : '—'} />
        <Numero rotulo={`Total vendas ${r()?.ano ?? ''}`} valor={moeda(r()?.totalVendas)} destaque />
        <Numero rotulo="A receber" valor={moeda(r()?.totalAReceber)} />
        <Numero rotulo="Ticket médio" valor={moeda(r()?.ticketMedio)} />
        <Numero
          rotulo="Média mensal × meta"
          valor={r()?.metaMensal ? `${moeda(r()!.realizadoMedioMensal)} · ${pct(r()!.percMetaMensal)}` : '—'}
          dica={r()?.metaMensal ? `Meta mensal ${moeda(r()!.metaMensal)} · ${r()!.mesesConsiderados} mês(es)` : 'Sem meta mensal'}
        />
        <Numero
          rotulo="Meta anual"
          valor={r()?.metaAnual ? pct(r()!.percMetaAnual) : '—'}
          dica={r()?.metaAnual ? `de ${moeda(r()!.metaAnual)}` : 'Sem meta anual'}
        />
        <Show when={props.podeEditar && r()}>
          <Button variant="ghost" size="sm" class="self-center" onClick={() => setMetasAberto(true)}>
            Metas
          </Button>
        </Show>
      </div>
      <div class="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filtrar por cor">
        <For each={props.status}>
          {(s) => (
            <button
              type="button"
              class="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs text-ink hover:bg-hover"
              classList={{
                'border-accent bg-hover font-medium': props.filtroStatus === s.id,
                'border-edge': props.filtroStatus !== s.id,
              }}
              aria-pressed={props.filtroStatus === s.id}
              onClick={() => props.aoFiltrar(props.filtroStatus === s.id ? null : s.id)}
            >
              <span class="size-2.5 rounded-full border border-black/25" style={{ background: corExibicao(s) }} />
              {s.nome}
              <span class="tabular-nums text-ink-subtle">{r()?.porStatus[s.codigo] ?? 0}</span>
            </button>
          )}
        </For>
        <Show when={(r()?.porStatus.sem_cor ?? 0) > 0 || props.filtroStatus === SEM_COR}>
          <button
            type="button"
            class="flex items-center gap-1.5 rounded-full border border-dashed px-2.5 py-1 text-xs text-ink hover:bg-hover"
            classList={{
              'border-accent bg-hover font-medium': props.filtroStatus === SEM_COR,
              'border-edge': props.filtroStatus !== SEM_COR,
            }}
            aria-pressed={props.filtroStatus === SEM_COR}
            title="Pedidos sem cor definida (para classificar à mão)"
            onClick={() => props.aoFiltrar(props.filtroStatus === SEM_COR ? null : SEM_COR)}
          >
            <span class="size-2.5 rounded-full border border-dashed border-ink-subtle" />
            Sem cor
            <span class="tabular-nums text-ink-subtle">{r()?.porStatus.sem_cor ?? 0}</span>
          </button>
        </Show>
        <Show when={props.filtroStatus !== null}>
          <button type="button" class="text-xs text-ink-subtle underline px-1" onClick={() => props.aoFiltrar(null)}>
            mostrar todos
          </button>
        </Show>
      </div>
      <Show when={r()}>
        <Metas aberto={metasAberto()} resumo={r()!} aoFechar={() => setMetasAberto(false)} aoSalvar={props.aoMudar} />
      </Show>
    </div>
  )
}

function Numero(props: { rotulo: string; valor: string; dica?: string; destaque?: boolean }) {
  return (
    <div class="flex flex-col" title={props.dica}>
      <span class="text-[11px] uppercase tracking-wide text-ink-subtle">{props.rotulo}</span>
      <span class="tabular-nums text-ink" classList={{ 'text-lg font-semibold': props.destaque, 'text-sm font-medium mt-1': !props.destaque }}>
        {props.valor}
      </span>
    </div>
  )
}

function Metas(props: { aberto: boolean; resumo: ResumoAnoPedidos; aoFechar: () => void; aoSalvar: () => void }) {
  const [mensal, setMensal] = createSignal('')
  const [anual, setAnual] = createSignal('')
  const [erro, setErro] = createSignal<string | null>(null)
  const abrir = () => {
    setMensal(numero(props.resumo.metaMensal))
    setAnual(numero(props.resumo.metaAnual))
    setErro(null)
  }
  createEffect(on(() => props.aberto, (a) => a && abrir()))
  const salvar = async (e: SubmitEvent) => {
    e.preventDefault()
    const m = lerNumeroBR(mensal())
    const a = lerNumeroBR(anual())
    if (Number.isNaN(m) || Number.isNaN(a)) return setErro('Use números, como 500.000,00.')
    try {
      await api.pedidos.definirMeta(props.resumo.ano, m, a)
      props.aoSalvar()
      props.aoFechar()
    } catch (x) {
      setErro(mensagemErro(x))
    }
  }
  const rotulo = 'flex flex-col gap-1.5 text-sm text-ink-muted'
  return (
    <Dialog
      open={props.aberto}
      onOpenChange={(o) => !o && props.aoFechar()}
      class="w-[min(420px,calc(100vw-32px))]"
    >
      <form onSubmit={salvar} class="flex flex-col gap-4 p-6">
        <h2 class="text-lg font-semibold text-ink">Metas de {props.resumo.ano}</h2>
        <label class={rotulo}>
          Meta mensal (R$)
          <Input value={mensal()} onInput={(e) => setMensal(e.currentTarget.value)} inputMode="decimal" />
        </label>
        <label class={rotulo}>
          Meta anual (R$)
          <Input value={anual()} onInput={(e) => setAnual(e.currentTarget.value)} inputMode="decimal" />
        </label>
        <Show when={erro()}>
          <p class="text-sm text-failure" role="alert">{erro()}</p>
        </Show>
        <div class="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent">Salvar</Button>
        </div>
      </form>
    </Dialog>
  )
}
